import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import {
  Alert,
  Button,
  Form,
  Input,
  InputNumber,
  Modal,
  Progress,
  Radio,
  Space,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import { useCallback, useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import { ApiError } from '../../api/errors';
import {
  ProjectTaskProgressMethod,
  addProjectTaskMilestone,
  changeProjectTaskProgressMethod,
  getProjectTaskMilestones,
  removeProjectTaskMilestone,
  setReachedProjectTaskMilestone,
} from '../../api/projects';
import type { ProjectTaskItem, ProjectTaskMilestoneSet } from '../../api/projects';
import { formatDateTime } from './projectDisplay';

/** Giá trị của biểu mẫu thêm mốc con. */
interface AddMilestoneFormValues {
  readonly name: string;
  readonly progressPercent: number;
}

/**
 * Hộp thoại mốc con của một công việc.
 *
 * <b>Người dùng chọn MỘT mốc — mốc mình đang đứng — chứ không tick từng cái.</b> Mốc con có thứ tự và
 * cộng dồn: đạt mốc <i>xong toàn bộ tính năng</i> thì đương nhiên đã qua mốc <i>chạy được luồng
 * chính</i>. Vì vậy giao diện dùng nhóm nút chọn một, không dùng ô đánh dấu — hình dạng của ô điều
 * khiển phải nói đúng bản chất của dữ liệu, nếu không người dùng sẽ thử làm điều không làm được.
 *
 * <b>Dành cho việc dài mà không chia nhỏ được thành các bước tick độc lập</b> — lập trình, thiết kế.
 * Việc có các bước rời rạc thì dùng danh sách kiểm hợp hơn.
 */
export function ProjectTaskMilestoneModal({
  projectId,
  task,
  canManage,
  canReportProgress,
  open,
  onClose,
}: {
  readonly projectId: string;
  readonly task: ProjectTaskItem | null;
  readonly canManage: boolean;
  readonly canReportProgress: boolean;
  readonly open: boolean;

  /** Đóng hộp thoại. Nhận cờ cho biết có gì đã đổi để nơi gọi tải lại cây công việc. */
  readonly onClose: (hasChanged: boolean) => void;
}): ReactNode {
  const [form] = Form.useForm<AddMilestoneFormValues>();

  const [milestoneSet, setMilestoneSet] = useState<ProjectTaskMilestoneSet | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanged, setHasChanged] = useState(false);

  useEffect(() => {
    if (!open || task === null) {
      return;
    }

    let isCurrent = true;

    async function load(taskId: string): Promise<void> {
      setErrorMessage(null);

      try {
        const loaded = await getProjectTaskMilestones(projectId, taskId);

        if (isCurrent) {
          setMilestoneSet(loaded);
        }
      } catch (error) {
        if (!isCurrent) {
          return;
        }

        setErrorMessage(
          error instanceof ApiError ? error.message : 'Không tải được chuỗi mốc con.',
        );
      }
    }

    void load(task.id);

    return () => {
      isCurrent = false;
    };
  }, [projectId, task, open]);

  const close = useCallback(() => {
    onClose(hasChanged);
    setHasChanged(false);
    setMilestoneSet(null);
  }, [hasChanged, onClose]);

  /** Chạy một thao tác đổi chuỗi mốc và nhận về chuỗi mới. */
  async function apply(
    action: (taskId: string) => Promise<ProjectTaskMilestoneSet>,
    failureMessage: string,
  ): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      setMilestoneSet(await action(task.id));
      setHasChanged(true);
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : failureMessage);
    } finally {
      setIsSaving(false);
    }
  }

  async function switchToMilestoneMethod(): Promise<void> {
    if (task === null) {
      return;
    }

    setIsSaving(true);

    try {
      await changeProjectTaskProgressMethod(
        projectId,
        task.id,
        ProjectTaskProgressMethod.Milestones,
      );

      setMilestoneSet(await getProjectTaskMilestones(projectId, task.id));
      setHasChanged(true);
      message.success('Từ giờ tiến độ của việc này lấy theo mốc xa nhất đã đạt.');
    } catch (error) {
      message.error(error instanceof ApiError ? error.message : 'Không đổi được cách đo.');
    } finally {
      setIsSaving(false);
    }
  }

  async function submit(values: AddMilestoneFormValues): Promise<void> {
    await apply(
      (taskId) =>
        addProjectTaskMilestone(projectId, taskId, {
          name: values.name,
          progressPercent: values.progressPercent,
          sequenceNumber: 0,
        }),
      'Không thêm được mốc con.',
    );

    form.resetFields();
  }

  const milestones = milestoneSet?.milestones ?? [];
  const isDriven = milestoneSet?.isProgressDrivenByMilestones ?? false;

  return (
    <Modal
      open={open}
      title={task === null ? 'Mốc con' : `Mốc con — ${task.wbsCode} ${task.name}`}
      width={760}
      footer={null}
      onCancel={close}
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {errorMessage !== null ? <Alert type="error" showIcon message={errorMessage} /> : null}

        {task?.isSummaryTask === true ? (
          <Alert
            type="info"
            showIcon
            message="Đây là một nhóm việc nên không có mốc con"
            description="Tiến độ của nhóm việc được tính từ các việc con lên. Hãy thêm mốc vào từng việc con."
          />
        ) : null}

        {milestoneSet !== null && !isDriven && task?.isSummaryTask === false ? (
          <Alert
            type="warning"
            showIcon
            message="Việc này chưa đo tiến độ bằng mốc con"
            description="Chọn mốc ở đây sẽ không làm tiến độ thay đổi — chuỗi mốc đang chỉ là kế hoạch chặng. Chuyển cách đo thì tiến độ sẽ lấy đúng phần trăm của mốc xa nhất đã đạt, và không gõ tay được nữa."
            action={
              canReportProgress ? (
                <Button
                  size="small"
                  type="primary"
                  loading={isSaving}
                  onClick={() => {
                    void switchToMilestoneMethod();
                  }}
                >
                  Đo bằng mốc con
                </Button>
              ) : null
            }
          />
        ) : null}

        {milestoneSet !== null ? (
          <Progress
            percent={Number(milestoneSet.progressPercent.toFixed(1))}
            status={isDriven ? 'active' : 'normal'}
          />
        ) : null}

        {milestones.length === 0 ? (
          <Typography.Text type="secondary">
            Chưa có mốc nào. Hãy chia việc này thành vài chặng có tên rõ ràng — ví dụ xong thiết kế
            sơ bộ, chạy được luồng chính, xong toàn bộ tính năng.
          </Typography.Text>
        ) : (
          <Radio.Group
            value={milestoneSet?.reachedMilestoneId ?? null}
            disabled={!canReportProgress || isSaving}
            style={{ width: '100%' }}
            onChange={(event) => {
              void apply(
                (taskId) =>
                  setReachedProjectTaskMilestone(
                    projectId,
                    taskId,
                    event.target.value as string | null,
                  ),
                'Không chọn được mốc.',
              );
            }}
          >
            <Space direction="vertical" size={8} style={{ width: '100%' }}>
              {/*
                Lựa chọn "chưa đạt mốc nào" phải hiện ra thành một dòng. Không có nó thì người chọn
                nhầm mốc đầu tiên không có cách nào quay lại trạng thái ban đầu.
              */}
              <Radio value={null}>
                <Typography.Text type="secondary">Chưa đạt mốc nào</Typography.Text>
              </Radio>

              {milestones.map((milestone) => (
                <Space key={milestone.id} size={8} wrap>
                  <Radio value={milestone.id}>
                    <span style={{ fontWeight: milestone.isReached ? 600 : 400 }}>
                      {milestone.name}
                    </span>
                  </Radio>
                  <Tag color={milestone.isReached ? 'success' : 'default'}>
                    {milestone.progressPercent.toString()}%
                  </Tag>
                  {milestone.isReached ? (
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      đạt {formatDateTime(milestone.reachedAt)}
                    </Typography.Text>
                  ) : null}
                  {canManage ? (
                    <Tooltip title="Xóa mốc này">
                      <Button
                        size="small"
                        danger
                        icon={<DeleteOutlined />}
                        onClick={() => {
                          void apply(
                            (taskId) =>
                              removeProjectTaskMilestone(projectId, taskId, milestone.id),
                            'Không xóa được mốc con.',
                          );
                        }}
                      />
                    </Tooltip>
                  ) : null}
                </Space>
              ))}
            </Space>
          </Radio.Group>
        )}

        {canManage && task?.isSummaryTask === false ? (
          <Form<AddMilestoneFormValues>
            form={form}
            layout="inline"
            onFinish={(values) => void submit(values)}
          >
            <Form.Item
              name="name"
              rules={[{ required: true, message: 'Chưa đặt tên mốc.' }]}
              style={{ flex: 1, minWidth: 260 }}
            >
              <Input placeholder="Tên mốc, ví dụ: chạy được luồng chính" />
            </Form.Item>
            <Form.Item
              name="progressPercent"
              rules={[{ required: true, message: 'Chưa nhập phần trăm.' }]}
            >
              <InputNumber<number>
                min={1}
                max={100}
                placeholder="%"
                style={{ width: 100 }}
              />
            </Form.Item>
            <Form.Item>
              <Button type="primary" htmlType="submit" icon={<PlusOutlined />} loading={isSaving}>
                Thêm mốc
              </Button>
            </Form.Item>
          </Form>
        ) : null}

        <Typography.Text type="secondary" style={{ fontSize: 12 }}>
          Phần trăm của các mốc phải tăng dần theo thứ tự thêm vào. Một mốc đứng sau mà phần trăm
          thấp hơn mốc trước sẽ làm tiến độ tụt xuống khi việc tiến lên, nên máy chủ từ chối.
        </Typography.Text>
      </Space>
    </Modal>
  );
}
